<template>
    <div class="logs-page">
        <BackendHeader />

        <div class="logs-container">
            <div class="logs-header">
                <h2>Error Logs</h2>
                <div class="logs-controls">
                    <select v-model="selectedFile" @change="loadFileContent" class="form-input file-selector">
                        <option value="">-- Select a log file --</option>
                        <option v-for="file in logFiles" :key="file.fileName" :value="file.fileName">
                            {{ file.fileName }} ({{ formatFileSize(file.fileSize) }} - {{ file.createdDate }})
                        </option>
                    </select>
                    <button @click="loadFiles" class="button button-primary" :disabled="loading">
                        {{ loading ? 'Loading...' : 'Refresh' }}
                    </button>
                    <button 
                        v-if="selectedFile" 
                        @click="deleteFile" 
                        class="button button-danger" 
                        :disabled="loading">
                        Delete File
                    </button>
                </div>
            </div>

            <div v-if="loading" class="loading-message">
                Loading...
            </div>

            <div v-else-if="error" class="error-message">
                {{ error }}
            </div>

            <div v-else-if="logFiles.length === 0" class="no-logs">
                No log files found.
            </div>

            <div v-else-if="!selectedFile" class="no-file-selected">
                Please select a log file from the dropdown above.
            </div>

            <div v-else-if="fileContent" class="logs-content">
                <div class="file-info">
                    <span>File: {{ selectedFile }}</span>
                    <span>Size: {{ formatFileSize(fileContent.length) }}</span>
                </div>
                <div class="log-content">
                    <pre>{{ fileContent }}</pre>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import BackendHeader from '@/components/BackendHeader.vue'
import api from '@/api'

interface LogFile {
    fileName: string
    fullPath: string
    createdDate: string
    fileSize: number
}

const logFiles = ref<LogFile[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const selectedFile = ref('')
const fileContent = ref('')

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const loadFiles = async () => {
    loading.value = true
    error.value = null
    
    try {
        const response = await api.getErrorLogFiles()
        logFiles.value = response.data.files || []
        
        // If a file was selected but no longer exists, clear selection
        if (selectedFile.value && !logFiles.value.find(f => f.fileName === selectedFile.value)) {
            selectedFile.value = ''
            fileContent.value = ''
        }
    } catch (err: any) {
        error.value = err.response?.data?.message || err.message || 'Failed to load log files'
        logFiles.value = []
    } finally {
        loading.value = false
    }
}

const loadFileContent = async () => {
    if (!selectedFile.value) {
        fileContent.value = ''
        return
    }

    loading.value = true
    error.value = null

    try {
        const response = await api.getErrorLogFileContent(selectedFile.value)
        fileContent.value = response.data.content || ''
    } catch (err: any) {
        error.value = err.response?.data?.message || err.message || 'Failed to load file content'
        fileContent.value = ''
    } finally {
        loading.value = false
    }
}

const deleteFile = async () => {
    if (!selectedFile.value) return

    if (!confirm(`Are you sure you want to delete "${selectedFile.value}"? This action cannot be undone.`)) {
        return
    }

    loading.value = true
    error.value = null

    try {
        await api.deleteErrorLogFile(selectedFile.value)
        selectedFile.value = ''
        fileContent.value = ''
        await loadFiles()
    } catch (err: any) {
        error.value = err.response?.data?.message || err.message || 'Failed to delete file'
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    loadFiles()
})
</script>

<style scoped>
.logs-page {
    min-height: 100vh;
    background-color: #f5f5f5;
    padding: 20px;
    text-align: left;
}

.logs-container {
    max-width: 1400px;
    margin: 0 auto;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 20px;
}

.logs-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #e0e0e0;
}

.logs-header h2 {
    margin: 0;
    color: #333;
}

.logs-controls {
    display: flex;
    gap: 10px;
    align-items: center;
}

.date-selector {
    min-width: 150px;
}

.loading-message,
.error-message,
.no-logs {
    text-align: center;
    padding: 40px;
    color: #666;
}

.error-message {
    color: #d32f2f;
    background-color: #ffebee;
    border-radius: 4px;
    padding: 15px;
}

.file-selector {
    min-width: 400px;
}

.no-file-selected {
    text-align: center;
    padding: 40px;
    color: #666;
    font-style: italic;
}

.file-info {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    margin-bottom: 15px;
    color: #666;
    font-size: 14px;
    border-bottom: 1px solid #e0e0e0;
}

.log-content {
    max-height: 70vh;
    overflow: auto;
    background: #263238;
    border-radius: 4px;
    padding: 15px;
}

.log-content pre {
    margin: 0;
    color: #aed581;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.5;
}

.button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
}

.button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.button-primary {
    background-color: #2196f3;
    color: white;
}

.button-primary:hover:not(:disabled) {
    background-color: #1976d2;
}

.button-danger {
    background-color: #f44336;
    color: white;
}

.button-danger:hover:not(:disabled) {
    background-color: #d32f2f;
}

.form-input {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.form-input:focus {
    outline: none;
    border-color: #2196f3;
}
</style>
